import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import AppLayout from './components/Layout/AppLayout'
import Login from './pages/Auth/Login'
import ForgotPassword from './pages/Auth/ForgotPassword'
import ResetPassword from './pages/Auth/ResetPassword'
import Setup from './pages/Auth/Setup'
import Dashboard from './pages/Dashboard'
import Employees from './pages/Employees'
import EmployeeDetails from './pages/EmployeeDetails'
import TeamDirectory from './pages/TeamDirectory'
import Leaves from './pages/Leaves'
import CompanyDocuments from './pages/CompanyDocuments'
import Holidays from './pages/Holidays'
import Profile from './pages/Profile'
import ApiTokens from './pages/ApiTokens'
import NotificationSettings from './pages/NotificationSettings'
import IssueSearch from './pages/ProjectCentral/IssueSearch'
import ProjectBacklog from './pages/ProjectCentral/ProjectBacklog'
import { useAuthStore } from './store/authStore'
import { authService } from './services/authService'
import { useActiveTimerSync } from './lib/useActiveTimerSync'
import { loginUrlFor, safeNext } from './lib/safeNext'
import OAuthConsent from './pages/OAuthConsent'
import Projects from './pages/ProjectCentral/Projects'
import ProjectBoard from './pages/ProjectCentral/ProjectBoard'
import ProjectList from './pages/ProjectCentral/ProjectList'
import ProjectTime from './pages/ProjectCentral/ProjectTime'
import ProjectSettings from './pages/ProjectCentral/ProjectSettings'
import ForYou from './pages/ProjectCentral/ForYou'
import TeamView from './pages/ProjectCentral/TeamView'
import TaskPage from './pages/ProjectCentral/TaskPage'
import BrowseTask from './pages/ProjectCentral/BrowseTask'
import BlogManager from './pages/AdminPanel/BlogManager'
import BlogEditor from './pages/AdminPanel/BlogEditor'
import AuthorManager from './pages/AdminPanel/AuthorManager'
import AuthorEditor from './pages/AdminPanel/AuthorEditor'
import MarketingEmailDashboard from './pages/MarketingEmail/Dashboard'
import Contacts from './pages/MarketingEmail/Contacts'
import ContactDetail from './pages/MarketingEmail/ContactDetail'
import ContactImport from './pages/MarketingEmail/ContactImport'
import CustomFields from './pages/MarketingEmail/CustomFields'
import Segments from './pages/MarketingEmail/Segments'
import SegmentEditor from './pages/MarketingEmail/SegmentEditor'
import SegmentDetail from './pages/MarketingEmail/SegmentDetail'
import Templates from './pages/MarketingEmail/Templates'
import TemplateEditor from './pages/MarketingEmail/TemplateEditor'
import TemplatePreview from './pages/MarketingEmail/TemplatePreview'
import Campaigns from './pages/MarketingEmail/Campaigns'
import CampaignWizard from './pages/MarketingEmail/CampaignWizard'
import CampaignDetail from './pages/MarketingEmail/CampaignDetail'
import SettingsPage from './pages/MarketingEmail/Settings'
import Suppression from './pages/MarketingEmail/Suppression'
import AuditLog from './pages/MarketingEmail/AuditLog'
import UnsubscribeLanding from './pages/MarketingEmail/UnsubscribeLanding'

function App() {
  const { isAuthenticated, user, updateUser } = useAuthStore()
  const location = useLocation()

  // Keep the global timer bar in sync with the server — timers can also be started or
  // stopped from another tab or by an AI agent over MCP.
  useActiveTimerSync(isAuthenticated)

  // Fetch user data with employee info when app loads and periodically sync
  useEffect(() => {
    if (!isAuthenticated) return

    const fetchUserData = async () => {
      try {
        const response = await authService.getCurrentUser()
        if (response.success) {
          updateUser(response.data.user)
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error)
      }
    }

    fetchUserData()

    // Periodically sync user data to pick up role changes
    const interval = setInterval(fetchUserData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [isAuthenticated, updateUser])

  // Public unsubscribe landing page — no auth shell, reachable whether logged in or not,
  // since it's opened from a link inside a real email.
  if (location.pathname === '/unsubscribed') {
    return <UnsubscribeLanding />
  }

  // Consent page for AI tools connecting by sign-in (OAuth): its own full-page layout. Error
  // messages from the server show without signing in; the request itself needs a session.
  const isConsentPage = location.pathname === '/oauth/consent'
  if (isConsentPage && (isAuthenticated || new URLSearchParams(location.search).has('error'))) {
    return <OAuthConsent />
  }

  // Just signed in on /login?next=…: continue to where the user was going (e.g. the consent
  // page). Handled here because signing in re-renders this component before Login can navigate.
  if (isAuthenticated && location.pathname === '/login') {
    return <Navigate to={safeNext(new URLSearchParams(location.search).get('next')) || '/dashboard'} replace />
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/setup/:token" element={<Setup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to={loginUrlFor(location)} replace />} />
      </Routes>
    )
  }

  return (
    <AppLayout>
      <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/employees/:id" element={<EmployeeDetails />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/team-directory" element={<TeamDirectory />} />
          <Route path="/holidays" element={<Holidays />} />
          <Route path="/leaves" element={<Leaves />} />
          <Route path="/company-documents" element={<CompanyDocuments />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/api-tokens" element={<ApiTokens />} />
          <Route path="/profile/notifications" element={<NotificationSettings />} />
          <Route path="/browse/:taskKey" element={<BrowseTask />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/for-you" element={<ForYou />} />
          <Route path="/projects/issues" element={<IssueSearch />} />
          <Route path="/projects/team-view" element={<TeamView />} />
          <Route path="/projects/:id/tasks/:taskId" element={<TaskPage />} />
          <Route path="/projects/:id/board" element={<ProjectBoard />} />
          <Route path="/projects/:id/list" element={<ProjectList />} />
          <Route path="/projects/:id/backlog" element={<ProjectBacklog />} />
          <Route path="/projects/:id/time" element={<ProjectTime />} />
          <Route path="/projects/:id/settings" element={<ProjectSettings />} />
          <Route path="/admin-panel" element={<BlogManager />} />
          <Route path="/admin-panel/blogs/new" element={<BlogEditor key={location.pathname} />} />
          <Route path="/admin-panel/blogs/edit/:id" element={<BlogEditor key={location.pathname} />} />
          <Route path="/admin-panel/authors" element={<AuthorManager />} />
          <Route path="/admin-panel/authors/new" element={<AuthorEditor key={location.pathname} />} />
          <Route path="/admin-panel/authors/edit/:id" element={<AuthorEditor key={location.pathname} />} />
          <Route path="/marketing-email" element={<MarketingEmailDashboard />} />
          <Route path="/marketing-email/contacts" element={<Contacts />} />
          <Route path="/marketing-email/contacts/fields" element={<CustomFields />} />
          <Route path="/marketing-email/contacts/import" element={<ContactImport />} />
          <Route path="/marketing-email/contacts/:id" element={<ContactDetail />} />
          <Route path="/marketing-email/campaigns" element={<Campaigns />} />
          <Route path="/marketing-email/campaigns/new" element={<CampaignWizard />} />
          <Route path="/marketing-email/campaigns/:id/edit" element={<CampaignWizard />} />
          <Route path="/marketing-email/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/marketing-email/templates" element={<Templates />} />
          <Route path="/marketing-email/templates/new" element={<TemplateEditor />} />
          <Route path="/marketing-email/templates/:id/edit" element={<TemplateEditor />} />
          <Route path="/marketing-email/templates/:id/preview" element={<TemplatePreview />} />
          <Route path="/marketing-email/segments" element={<Segments />} />
          <Route path="/marketing-email/segments/new" element={<SegmentEditor />} />
          <Route path="/marketing-email/segments/:id/edit" element={<SegmentEditor />} />
          <Route path="/marketing-email/segments/:id" element={<SegmentDetail />} />
          <Route path="/marketing-email/suppression" element={<Suppression />} />
          <Route path="/marketing-email/audit-log" element={<AuditLog />} />
          <Route path="/marketing-email/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    </AppLayout>
  )
}

export default App
